import React from 'react';
import { getErrorMessage } from '../../utils/errorMessages';
import { ui } from '../../config/uiSizes';

interface ErrorAlertProps {
    /** Error code (from errorMessages.ts) or a literal user-safe message string. */
    message: string;
    /** When provided, a Retry button is shown that calls this callback. */
    onRetry?: () => void;
    /** Optional extra classes applied to the outer wrapper. */
    className?: string;
}

/**
 * Inline error banner matching the dashboard error pattern.
 * Pass a code from errorMessages.ts (e.g. 'fetch_failed') or any user-safe string.
 * Raw API error text, exception messages, and HTTP status codes must NOT be passed here.
 */
const ErrorAlert: React.FC<ErrorAlertProps> = ({ message, onRetry, className = '' }) => {
    const { title, detail } = getErrorMessage(message);

    return (
        <div
            role="alert"
            className={`flex items-start ${ui.gap.md} rounded-lg border border-red-200 bg-red-50 ${ui.page.sectionPadding} dark:border-red-700 dark:bg-red-900/20 ${className}`}
        >
            <div className="flex-shrink-0 mt-0.5">
                <svg
                    className={`${ui.svg.sm} text-red-500 dark:text-red-400`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                    />
                </svg>
            </div>
            <div className="flex-1 min-w-0">
                <p className={`${ui.text.body} font-semibold text-red-800 dark:text-red-200`}>{title}</p>
                <p className={`${ui.text.xs} text-red-700 dark:text-red-300 mt-1`}>{detail}</p>
            </div>
            {onRetry && (
                <button
                    type="button"
                    onClick={onRetry}
                    className={`flex-shrink-0 ${ui.button.sm} bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white rounded-lg transition-colors font-semibold`}
                >
                    Retry
                </button>
            )}
        </div>
    );
};

export default ErrorAlert;
